import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, hasMinRole } from "@/context/AuthContext";
import { PageHeader, Card, Loading, EmptyState } from "@/components/common";
import { GettingStarted } from "@/components/GettingStarted";
import { WorkflowBadge } from "@/components/insights/InsightStatusBadges";
import { fmtDateTime, daysWaiting } from "@/lib/insightsApi";
import { fmtMeetingDate, MEETING_STATUS } from "@/lib/meetingsApi";
import { Inbox, RotateCcw, FilePen, GitPullRequestArrow, CheckCircle2, ArrowRight, Plus, CalendarDays, ListChecks } from "lucide-react";

function WorkItem({ it }) {
  const age = daysWaiting(it.updated_at);
  return (
    <Link to={`/insights/${it.insight_id}`} data-testid="work-item"
      className="flex items-center gap-3 rounded-lg border border-brand-line bg-white px-4 py-3 hover:border-brand-civic/40 hover:shadow-card transition-all">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-brand-ink text-sm truncate">{it.title}</span>
          <WorkflowBadge status={it.workflow_status} />
        </div>
        <div className="text-xs text-slate-500 mt-0.5 truncate">
          {it.insight_type_code || "—"} · Dept {it.department_id?.replace("dept_", "") || "—"}
          {age != null && <> · {age === 0 ? "today" : `${age}d waiting`}</>}
          {" · updated "}{fmtDateTime(it.updated_at)}
        </div>
      </div>
      <ArrowRight className="h-4 w-4 text-slate-300 shrink-0" />
    </Link>
  );
}

function Section({ icon: Icon, title, items, testid, emptyText }) {
  return (
    <Card data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className="h-8 w-8 rounded-lg bg-brand-civic-50 flex items-center justify-center">
          <Icon className="h-4 w-4 text-brand-civic" />
        </span>
        <h2 className="font-display font-bold text-brand-ink text-base">{title}</h2>
        <span className="ml-auto text-xs font-semibold text-slate-400" data-testid={`${testid}-count`}>{items.length}</span>
      </div>
      {items.length === 0
        ? <p className="text-sm text-slate-400 py-4 text-center">{emptyText}</p>
        : <div className="space-y-2">{items.slice(0, 8).map((it) => <WorkItem key={it.insight_id} it={it} />)}</div>}
    </Card>
  );
}

export default function MyWork() {
  const { user } = useAuth();
  const [insights, setInsights] = useState(null);
  const [adv, setAdv] = useState({ actionable: [], returned: [], drafts: [], recent: [] });
  const [meetings, setMeetings] = useState([]);
  const [isBoard, setIsBoard] = useState(false);
  const [error, setError] = useState("");
  const approver = hasMinRole(user?.role, "manager");
  const uid = user?.user_id;

  useEffect(() => {
    api.get("/insights?limit=200")
      .then((r) => setInsights(r.data.insights))
      .catch(() => setError("We couldn't load your work right now. Please try again."));
    api.get("/workflow/my-work").then((r) => setAdv(r.data)).catch(() => {});
    api.get("/forum/my-memberships").then((r) => setIsBoard(!!r.data?.is_board_user)).catch(() => {});
    if (hasMinRole(user?.role, "manager")) {
      api.get("/meetings?upcoming=true&limit=50").then((r) => setMeetings(r.data.meetings)).catch(() => {});
    }
  }, [user]);

  if (error) return <EmptyState icon={Inbox} title="Unable to load" description={error} />;
  if (!insights) return <Loading label="Loading your work…" />;

  const requiresApproval = approver ? insights.filter((i) => ["submitted", "in_review"].includes(i.workflow_status)) : [];
  const returnedToMe = insights.filter((i) => i.workflow_status === "rejected" && i.created_by === uid);
  const myDrafts = insights.filter((i) => i.workflow_status === "draft" && i.created_by === uid);
  const inProgress = insights.filter((i) => i.created_by === uid && ["submitted", "in_review"].includes(i.workflow_status));
  const recentlyCompleted = insights
    .filter((i) => ["approved", "denied"].includes(i.workflow_status))
    .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || "")).slice(0, 8);
  const myScheduled = insights.filter((i) => i.created_by === uid && i.agenda_status === "scheduled");
  const agendaDrafts = meetings.filter((m) => m.status === "draft");
  const upcomingMeetings = meetings.filter((m) => ["scheduled", "published", "in_progress"].includes(m.status));
  const hasUrgent = requiresApproval.length > 0 || returnedToMe.length > 0;
  const gsProps = { user, uid, insights, meetings, isBoard };

  return (
    <div data-testid="my-work-page">
      <PageHeader
        title="My Work"
        subtitle="Your queue across the Insight workflow — actionable items first."
        actions={hasMinRole(user?.role, "analyst") && (
          <Link to="/insights/new" data-testid="mywork-create-insight"
            className="inline-flex items-center gap-2 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900 transition-colors">
            <Plus className="h-4 w-4" /> New Insight
          </Link>
        )}
      />
      {!hasUrgent && adv.actionable.length === 0 && <GettingStarted {...gsProps} />}
      {adv.actionable.length > 0 && (
        <Card data-testid="section-awaiting-my-action" className="mb-5 border-brand-civic/40">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-8 w-8 rounded-lg bg-brand-civic-50 flex items-center justify-center"><GitPullRequestArrow className="h-4 w-4 text-brand-civic" /></span>
            <h2 className="font-display font-bold text-brand-ink text-base">Awaiting My Action</h2>
            <span className="ml-auto text-xs font-semibold text-slate-400" data-testid="section-awaiting-my-action-count">{adv.actionable.length}</span>
          </div>
          <div className="space-y-2">
            {adv.actionable.map((it) => (
              <Link key={it.insight_id} to={`/insights/${it.insight_id}`} data-testid="advanced-work-item"
                className="flex items-center gap-3 rounded-lg border border-brand-line bg-white px-4 py-3 hover:border-brand-civic/40 hover:shadow-card transition-all">
                <div className="min-w-0 flex-1">
                  <span className="font-semibold text-brand-ink text-sm truncate block">{it.title}</span>
                  <div className="text-xs text-slate-500 mt-0.5">Waiting on: <span className="font-medium text-brand-ink">{it.current_step_name}</span></div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300 shrink-0" />
              </Link>
            ))}
          </div>
        </Card>
      )}
      <div className="grid gap-5 lg:grid-cols-2">
        {approver && (
          <Section icon={GitPullRequestArrow} title="Requires My Approval" items={requiresApproval}
            testid="section-requires-approval" emptyText="No Insights are waiting on your approval." />
        )}
        <Section icon={RotateCcw} title="Returned to Me" items={returnedToMe}
          testid="section-returned" emptyText="Nothing has been returned for revision." />
        <Section icon={FilePen} title="My Drafts" items={myDrafts}
          testid="section-drafts" emptyText="You have no draft Insights yet." />
        <Section icon={Inbox} title="In Progress" items={inProgress}
          testid="section-in-progress" emptyText="Nothing of yours is moving through workflow." />
        <Section icon={CheckCircle2} title="Recently Completed" items={recentlyCompleted}
          testid="section-completed" emptyText="No recently completed Insights." />
        {!approver && (
          <Section icon={CalendarDays} title="Upcoming Agenda Items" items={myScheduled}
            testid="section-my-scheduled" emptyText="None of your Insights are scheduled yet." />
        )}
      </div>

      {approver && (
        <div className="grid gap-5 lg:grid-cols-2 mt-5" data-testid="clerk-sections">
          <MeetingSection icon={ListChecks} title="Agenda Drafts" meetings={agendaDrafts}
            testid="section-agenda-drafts" emptyText="No draft agendas." />
          <MeetingSection icon={CalendarDays} title="Upcoming Meetings" meetings={upcomingMeetings}
            testid="section-upcoming-meetings" emptyText="No upcoming meetings scheduled." />
        </div>
      )}
      {hasUrgent && <div className="mt-5"><GettingStarted {...gsProps} /></div>}
    </div>
  );
}

function MeetingSection({ icon: Icon, title, meetings, testid, emptyText }) {
  return (
    <Card data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className="h-8 w-8 rounded-lg bg-brand-civic-50 flex items-center justify-center"><Icon className="h-4 w-4 text-brand-civic" /></span>
        <h2 className="font-display font-bold text-brand-ink text-base">{title}</h2>
        <span className="ml-auto text-xs font-semibold text-slate-400">{meetings.length}</span>
      </div>
      {meetings.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">{emptyText}</p> : (
        <div className="space-y-2">
          {meetings.slice(0, 8).map((m) => (
            <Link key={m.meeting_id} to={`/meetings/${m.meeting_id}`} data-testid="clerk-meeting-item"
              className="flex items-center gap-3 rounded-lg border border-brand-line bg-white px-4 py-3 hover:border-brand-civic/40 hover:shadow-card transition-all">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-brand-ink text-sm truncate">{m.title}</span>
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${(MEETING_STATUS[m.status] || {}).cls}`}>{(MEETING_STATUS[m.status] || {}).label}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{m.governing_body_name} · {fmtMeetingDate(m.date, m.start_time)}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-300 shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}
